import React, { lazy, Suspense, useEffect, useState } from 'react';
import { ErrorBoundary } from '@sentry/react';
import { PageLoading, PageLoadError } from './components/PageLoading';
import { MainApp } from './MainApp';
import { STORY_NAVIGATION_EVENT, isThreeSecStoryPath } from './features/story/navigation';
import { CustomerView, hasCustomerPageQuery, isCustomerPath } from './views/customer';
import { CustomC2BillingTestView, isCustomC2BillingTestPath } from './views/custom-c2-billing-test';
import {
  hasPrintsContactPageQuery,
  hasPrintsTokushoPageQuery,
  isPrintsContactPath,
  isPrintsTokushoPath,
} from './components/prints/printsLegal';

const ThreeSecStoryApp = lazy(() => import('./features/story/ThreeSecStoryApp').then((module) => ({ default: module.ThreeSecStoryApp })));
const ImportFileView = lazy(() => import('./views/import-file').then((module) => ({ default: module.ImportFileView })));
const BooksView = lazy(() => import('./views/books').then((module) => ({ default: module.BooksView })));
const PrintsView = lazy(() => import('./views/prints').then((module) => ({ default: module.PrintsView })));
const PrintsV2View = lazy(() => import('./views/printv2').then((module) => ({ default: module.PrintsV2View })));
const PrintsContactView = lazy(() => import('./views/prints-contact').then((module) => ({ default: module.PrintsContactView })));
const PrintsTokushoView = lazy(() => import('./views/prints-tokusho').then((module) => ({ default: module.PrintsTokushoView })));
const TiconView = lazy(() => import('./views/ticon').then((module) => ({ default: module.TiconView })));
const CheeseView = lazy(() => import('./views/cheese').then((module) => ({ default: module.CheeseView })));
const InviteFriendView = lazy(() => import('./views/invite-friend').then((module) => ({ default: module.InviteFriendView })));
const VerifyAccountView = lazy(() => import('./views/verify-account').then((module) => ({ default: module.VerifyAccountView })));
const ReportView = lazy(() => import('./views/report').then((module) => ({ default: module.ReportView })));
const BabyManagementView = lazy(() => import('./views/baby').then((module) => ({ default: module.BabyManagementView })));
const LetterView = lazy(() => import('./views/letter').then((module) => ({ default: module.LetterView })));
const CustomC2View = lazy(() => import('./views/custom-c2').then((module) => ({ default: module.CustomC2View })));

const getCurrentPathname = (): string => {
  if (typeof window === 'undefined') {
    return '/';
  }

  return window.location.pathname;
};

const isCustomC2Path = (pathname: string): boolean => {
  const normalized = pathname.length > 1 && pathname.endsWith('/')
    ? pathname.slice(0, -1)
    : pathname;
  return normalized === '/custom/c2'
    || normalized === '/custom/c2/gift'
    || normalized === '/custom/c2/payment/success'
    || normalized === '/custom/c2/payment/fail';
};

const isRecordMovePath = (pathname: string): boolean => {
  const normalized = pathname.length > 1 && pathname.endsWith('/')
    ? pathname.slice(0, -1)
    : pathname;
  return normalized === '/move';
};

const hasRecordMovePageQuery = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return new URLSearchParams(window.location.search).get('page') === 'move';
  } catch {
    return false;
  }
};

const isTiconPath = (pathname: string): boolean => {
  const normalized = pathname.length > 1 && pathname.endsWith('/')
    ? pathname.slice(0, -1)
    : pathname;
  return normalized === '/ticon';
};

const hasTiconPageQuery = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return new URLSearchParams(window.location.search).get('page') === 'ticon';
  } catch {
    return false;
  }
};

const isPrintsPath = (pathname: string, route = '/prints'): boolean => {
  const normalized = pathname.length > 1 && pathname.endsWith('/')
    ? pathname.slice(0, -1)
    : pathname;
  return normalized === route;
};

const hasPrintsPageQuery = (page = 'prints'): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return new URLSearchParams(window.location.search).get('page') === page;
  } catch {
    return false;
  }
};

const isBooksPath = (pathname: string): boolean => {
  const normalized = pathname.length > 1 && pathname.endsWith('/')
    ? pathname.slice(0, -1)
    : pathname;
  return normalized === '/books';
};

const hasBooksPageQuery = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return new URLSearchParams(window.location.search).get('page') === 'books';
  } catch {
    return false;
  }
};

const isCheesePath = (pathname: string): boolean => {
  const normalized = pathname.length > 1 && pathname.endsWith('/')
    ? pathname.slice(0, -1)
    : pathname;
  return normalized === '/cheese';
};

const hasCheesePageQuery = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return new URLSearchParams(window.location.search).get('page') === 'cheese';
  } catch {
    return false;
  }
};

const isInviteFriendPath = (pathname: string): boolean => {
  const normalized = pathname.length > 1 && pathname.endsWith('/')
    ? pathname.slice(0, -1)
    : pathname;
  return normalized === '/service/inviteFriend';
};

const isVerifyAccountPath = (pathname: string): boolean => {
  const normalized = pathname.length > 1 && pathname.endsWith('/')
    ? pathname.slice(0, -1)
    : pathname;
  return normalized === '/service/verifyAccount';
};

const isReportPath = (pathname: string): boolean => {
  const normalized = pathname.length > 1 && pathname.endsWith('/')
    ? pathname.slice(0, -1)
    : pathname;
  return normalized === '/report';
};

const hasReportPageQuery = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return new URLSearchParams(window.location.search).get('page') === 'report';
  } catch {
    return false;
  }
};

const isLetterPath = (pathname: string): boolean => {
  const normalized = pathname.length > 1 && pathname.endsWith('/')
    ? pathname.slice(0, -1)
    : pathname;
  return normalized === '/letter';
};

const hasLetterPageQuery = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return new URLSearchParams(window.location.search).get('page') === 'letter';
  } catch {
    return false;
  }
};

const isBabyManagementPath = (pathname: string): boolean => {
  const normalized = pathname.length > 1 && pathname.endsWith('/')
    ? pathname.slice(0, -1)
    : pathname;
  return normalized === '/baby';
};

const hasBabyManagementPageQuery = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return new URLSearchParams(window.location.search).get('page') === 'baby';
  } catch {
    return false;
  }
};

const AppRoutes: React.FC = () => {
  const [pathname, setPathname] = useState<string>(() => getCurrentPathname());

  useEffect(() => {
    const syncPathname = () => {
      setPathname(getCurrentPathname());
    };

    window.addEventListener('popstate', syncPathname);
    window.addEventListener(STORY_NAVIGATION_EVENT, syncPathname);

    return () => {
      window.removeEventListener('popstate', syncPathname);
      window.removeEventListener(STORY_NAVIGATION_EVENT, syncPathname);
    };
  }, []);

  if (isThreeSecStoryPath(pathname)) {
    return <ThreeSecStoryApp pathname={pathname} />;
  }

  if (isRecordMovePath(pathname) || hasRecordMovePageQuery()) {
    return <ImportFileView />;
  }

  if (isTiconPath(pathname) || hasTiconPageQuery()) {
    return <TiconView />;
  }

  if (isPrintsTokushoPath(pathname) || hasPrintsTokushoPageQuery()) {
    return <PrintsTokushoView />;
  }

  if (isPrintsContactPath(pathname) || hasPrintsContactPageQuery()) {
    return <PrintsContactView />;
  }

  if (isPrintsPath(pathname, '/printv2') || hasPrintsPageQuery('printv2')) {
    return <PrintsV2View />;
  }

  if (isPrintsPath(pathname) || hasPrintsPageQuery()) {
    return <PrintsView />;
  }

  if (isBooksPath(pathname) || hasBooksPageQuery()) {
    return <BooksView />;
  }

  if (isCheesePath(pathname) || hasCheesePageQuery()) {
    return <CheeseView />;
  }

  if (isInviteFriendPath(pathname)) {
    return <InviteFriendView />;
  }

  if (isVerifyAccountPath(pathname)) {
    return <VerifyAccountView />;
  }

  if (isReportPath(pathname) || hasReportPageQuery()) {
    return <ReportView />;
  }

  if (isBabyManagementPath(pathname) || hasBabyManagementPageQuery()) {
    return <BabyManagementView />;
  }

  if (isLetterPath(pathname) || hasLetterPageQuery()) {
    return <LetterView />;
  }

  if (isCustomerPath(pathname) || hasCustomerPageQuery()) {
    return <CustomerView />;
  }

  if (isCustomC2BillingTestPath(pathname)) {
    return <CustomC2BillingTestView />;
  }

  if (isCustomC2Path(pathname)) {
    return <CustomC2View />;
  }

  return <MainApp />;
};

export const App: React.FC = () => (
  <ErrorBoundary fallback={<PageLoadError />}>
    <Suspense fallback={<PageLoading fullScreen />}>
      <AppRoutes />
    </Suspense>
  </ErrorBoundary>
);
